import { useMemo } from 'react'

import { useGetConventionalGenreListQuery, useGetConventionalSubgenreListQuery, useGetGenreTaxonomyQuery } from '../../../api/core'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { Game } from '../../game'
import { useGetGameList } from '../../game/hooks/gameHooks'

export const useGetCategoriesGenreMap = () => {
  const userLanguage = useCurrentUserLanguage()
  const { data: subGenreListDetails } = useGetConventionalSubgenreListQuery({ userLanguage })
  const { data: genreListDetails } = useGetConventionalGenreListQuery({ userLanguage })
  const { data: genreTaxonomy } = useGetGenreTaxonomyQuery({ userLanguage })

  const categoriesGenreMap = useMemo(() => {
    if (!genreTaxonomy) {
      return
    }

    return genreTaxonomy
      .map(({ genres, name: categoryName }) =>
        genres.map((genre) => {
          const genreInfo = subGenreListDetails?.find((subGenreInfo) => genre.id === subGenreInfo.parentGenreId)
          const mappedSubgenres = genre.subgenres.map((subGenreId) => {
            const subGenreInfo = subGenreListDetails?.find((subGenreListDetail) => subGenreListDetail.id === subGenreId.id)
            return subGenreInfo
          })
          const genreDescription = genreListDetails?.find((genreInfo) => genre.id === genreInfo.id)
          return { ...genre, categoryName, ...genreInfo, genreDescription, mappedSubgenres }
        })
      )
      .flat()
  }, [genreTaxonomy, subGenreListDetails, genreListDetails])

  return categoriesGenreMap
}

export const useGetGameInfoList = () => {
  const categoriesGenreMap = useGetCategoriesGenreMap()

  const gameIds = useMemo(() => {
    if (!categoriesGenreMap) return
    const filteredGameIds = categoriesGenreMap.flatMap((genre) => genre.exampleAppIds ?? [])
    const subGenreExampleAppIds = categoriesGenreMap.flatMap((genre) => genre.mappedSubgenres.flatMap((subGenre) => subGenre?.exampleAppIds ?? []))
    return [...new Set([...filteredGameIds, ...subGenreExampleAppIds])].filter((id): id is number => id != null)
  }, [categoriesGenreMap])

  const { data: gamesData } = useGetGameList(
    {
      gameIds: gameIds as number[],
      include: 'name,names,artist,icon,icons,appId,conventionalSubgenreId',
    },
    { skip: !gameIds || gameIds.length === 0 }
  )

  const gamesDataList = useMemo(() => {
    if (!gamesData) return
    return gamesData.reduce((result, game) => {
      result[game.appId] = game
      return result
    }, {} as { [appId: string]: Game })
  }, [gamesData])

  return { gamesData, gamesDataList }
}
